import { useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import { IconCheck, IconX } from "@tabler/icons-react";
import { classNames } from "@ct-react/core";
import { User, UserInput } from "@shared/graphql";
import { validators } from "../../tools/form";
import { actions, form } from "../../i18n/sharable-defs";
import { useForm } from "../../hooks/form";
import { useLazyLocaleApi } from "../../hooks/locale-api";
import { useTools } from "../../hooks/tools";
import { Wrapper } from "../../components/layout/wrapper";
import Button from "../../components/common/button";
import "./profile.scss";
import Skeleton from "react-loading-skeleton";

const transDefs = defineMessages({
  title: { id: "profile.title", defaultMessage: "Mon profil utilisateur" },
  sent: { id: "profile.sent", defaultMessage: "Vos données on été sauvegardées" },
  failed: { id: "profile.failed", defaultMessage: "Une erreur est survenue à la sauvegarde" }
});

const ProfilePage = () => {

  const intl = useIntl();
  const { countries } = useTools();
  const [ getUser, { loading } ] = useLazyLocaleApi<User>("/user");
  const [ saveUser ] = useLazyLocaleApi<User, UserInput>("/user", "PUT");
  const { register, reset, formState, handleSubmit } = useForm<User>();

  useEffect(() => {
    getUser().then(reset);
  }, []);

  const submit = async ({ id, username, email, ...user }: Partial<User>) => {
    await saveUser(user).then(() => void 0);
  }

  if (loading)
    return (
      <Wrapper.Centered as="main" className="profile">
        <Skeleton style={{ height: "32rem" }} borderRadius=".625rem" />
      </Wrapper.Centered>);

  return (
    <Wrapper.Centered as="main" className="profile">
      <div className="boxed">
        <h2>{intl.formatMessage(transDefs.title)}</h2>
        <form onSubmit={handleSubmit(submit, { resetStateSubmitTimer: 5000 })}>

          <div className="part user">

            <div className="f-group">
              <input type="text"
                     {...register("username", { readOnly: true })} />
              <p className="f-error" />
            </div>

            <div className="f-group">
              <input type="email"
                     {...register("email", { readOnly: true })} />
              <p className="f-error" />
            </div>

            <div className="f-group">
              <input type="text"
                     className={classNames({ dirty: "firstName" in formState.dirtyFields })}
                     {...register("firstName", { required: intl.formatMessage(form.emptyError) })}
                     placeholder={intl.formatMessage(form.firstName)}
                     autoComplete="given-name" />
              <p className="f-error">
                {!!formState.dirtyFields["firstName"] && formState.dirtyFields["firstName"].error}
              </p>
            </div>

            <div className="f-group">
              <input type="text"
                     className={classNames({ dirty: "lastName" in formState.dirtyFields })}
                     {...register("lastName", { required: intl.formatMessage(form.emptyError) })}
                     placeholder={intl.formatMessage(form.lastName)}
                     autoComplete="family-name" />
              <p className="f-error">
                {!!formState.dirtyFields["lastName"] && formState.dirtyFields["lastName"].error}
              </p>
            </div>

            <div className="f-group">
              <input type="tel"
                     className={classNames({ dirty: "phone" in formState.dirtyFields })}
                     {...register("phone", {
                       required: intl.formatMessage(form.emptyError),
                       validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError)) }
                     })}
                     placeholder={intl.formatMessage(form.phone)}
                     autoComplete="tel" />
              <p className="f-error">
                {!!formState.dirtyFields["phone"] && formState.dirtyFields["phone"].error}
              </p>
            </div>

          </div>

          <div className="part address">

            <div className="f-group">
              <div className="fl-group">
                <input type="text"
                       className={classNames({ dirty: "address.street" in formState.dirtyFields })}
                       {...register("address.street", { required: intl.formatMessage(form.emptyError) })}
                       placeholder={intl.formatMessage(form.street)}
                       autoComplete="street-address" />
                <input type="text"
                       className={classNames("street-min", { dirty: "address.streetNumber" in formState.dirtyFields })}
                        {...register("address.streetNumber")}
                       placeholder={intl.formatMessage(form.streetNumber)} />
              </div>
              <p className="f-error">
                {!!formState.dirtyFields["address.street"] && formState.dirtyFields["address.street"].error}
              </p>
            </div>

            <div className="f-group">
              <input type="text"
                     className={classNames({ dirty: "address.streetComplement" in formState.dirtyFields })}
                     {...register("address.streetComplement")}
                     placeholder={intl.formatMessage(form.streetComplement)} />
              <p className="f-error" />
            </div>

            <div className="f-group">
              <div className="fl-group">
                <input type="text"
                       className={classNames("zip-min", { dirty: "address.zip" in formState.dirtyFields })}
                       {...register("address.zip", {
                         required: intl.formatMessage(form.emptyError)
                       })}
                       placeholder={intl.formatMessage(form.zip)}
                       autoComplete="postal-code" />
                <input type="text"
                       {...register("address.city", {
                         required: intl.formatMessage(form.emptyError)
                       })}
                       placeholder={intl.formatMessage(form.city)} />
              </div>
              <p className="f-error">
                {(!!formState.dirtyFields["address.zip"] || !!formState.dirtyFields["address.city"]) &&
                  (formState.dirtyFields["address.zip"] || formState.dirtyFields["address.city"]).error
                }
              </p>
            </div>

            <div className="f-group">
              <select className={classNames({ dirty: "address.country" in formState.dirtyFields })}
                      {...register("address.country", {
                        required: intl.formatMessage(form.emptyError)
                      })}
                      autoComplete="country">
                <option value="">{intl.formatMessage(form.countryPlaceholder)}</option>
                {Object.entries(countries).map(([ value, label ], index) => (
                  <option key={index} value={value}>{label}</option>
                ))}
              </select>
              <p className="f-error">
                {!!formState.dirtyFields["address.country"] && formState.dirtyFields["address.country"].error}
              </p>
            </div>

          </div>

          <div className="part submit">
            <Button type="submit"
                    className="primary"
                    processing={formState.submit.inProcess}
                    disabled={!formState.isValid}>
              {intl.formatMessage(actions.save)}
            </Button>
            {(formState.submit.finished) &&
              <span className="callback">
                {(formState.submit.succeeded)
                  ? <><IconCheck className="s-icon" />{intl.formatMessage(transDefs.sent)}</>
                  : <><IconX className="f-icon" />{intl.formatMessage(transDefs.failed)}</>
                }
              </span>
            }
          </div>

        </form>
      </div>
    </Wrapper.Centered>);

}

export default ProfilePage;
